import React, { useCallback, useEffect, useState } from 'react';
import type { RacingLicenseDetailDto, RacingLicenseDto, RacingLicenseStatus } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { Empty, ErrorBanner, Field, Modal, Panel, Pill } from '../ui';
import { ask } from '../dialogs';

/**
 * SlipAngle: le licenze del gioco. Una licenza = una persona, con un codice da scrivere nel programma
 * «SlipAngle», quanti PC al massimo, una scadenza facoltativa. Senza licenza il gioco non si scarica e non parte.
 */

const STATUS: Record<RacingLicenseStatus, { label: string; tone: 'good' | 'warn' | 'bad' | 'soft'; tip: string }> = {
  attiva: { label: 'Attiva', tone: 'good', tip: 'Almeno un PC ha attivato la licenza e può giocare.' },
  'mai-usata': { label: 'Mai usata', tone: 'soft', tip: 'Nessun PC ha ancora scritto questo codice.' },
  sospesa: { label: 'Sospesa', tone: 'warn', tip: 'Nessun PC può giocare né aggiornare finché non la riattivi.' },
  scaduta: { label: 'Scaduta', tone: 'bad', tip: 'La data di scadenza è passata: i PC non possono più giocare.' },
};

const EVENT: Record<string, string> = {
  creata: 'Licenza creata',
  importata: 'Importata dal sistema di prima',
  attivato: 'PC attivato',
  riattivato: 'PC attivato di nuovo (reinstallato)',
  'troppi-pc': 'Rifiutato: troppi PC',
  rifiutato: 'Rifiutato',
  'pc-liberato': 'PC liberato',
  'codice-nuovo': 'Codice nuovo',
  sospesa: 'Sospesa',
  riattivata: 'Riattivata',
  modificata: 'Modificata',
};

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const day = (d: string | null) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : 'mai');
const pcs = (n: number) => (n === 1 ? '1 PC' : `${n} PC`);

export function RacingLicensesPage({ ctx }: { ctx: Ctx }) {
  const [list, setList] = useState<RacingLicenseDto[] | null>(null);
  const [ready, setReady] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState<{ holder: string; code: string } | null>(null);
  const openId = typeof ctx.sub.lic === 'number' ? ctx.sub.lic : null;

  const load = useCallback(async () => {
    try {
      const r = await ctx.api.get<{ licenses: RacingLicenseDto[]; ready: boolean }>('/api/racing/licenses');
      setList(r.licenses);
      setReady(r.ready);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [ctx.api]);
  useEffect(() => {
    void load();
  }, [load]);

  if (openId) {
    return (
      <>
        <LicenseDetail ctx={ctx} id={openId} onBack={() => { ctx.setSub({ lic: null }); void load(); }} onCode={setCode} />
        {code && <CodeModal holder={code.holder} code={code.code} onClose={() => setCode(null)} />}
      </>
    );
  }

  const active = (list ?? []).filter((l) => l.status === 'attiva').length;
  const devices = (list ?? []).reduce((n, l) => n + l.devicesActive, 0);
  return (
    <div className="panels single">
      <Panel
        title="Licenze SlipAngle"
        subtitle={list ? `${list.length === 1 ? '1 licenza' : `${list.length} licenze`} · ${active} attive · ${pcs(devices)} che possono giocare` : undefined}
        right={
          <button type="button" className="btn dark" title="Crea il codice da dare a chi deve giocare: lo scrive nel programma «SlipAngle»" onClick={() => setCreating(true)}>
            <Icon name="plus" size={16} /> Nuova licenza
          </button>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {!ready && (
          <div className="error" title="Sul server mancano la chiave che firma le licenze (RACING_SIGNING_KEY_FILE) o il segreto del distributore (RACING_AGENT_SECRET).">
            <span>Il server non è ancora pronto ad attivare i PC: puoi creare licenze, ma nessuno può attivarle.</span>
          </div>
        )}
        {!list ? (
          <div className="muted small">Caricamento…</div>
        ) : list.length === 0 ? (
          <Empty icon="key">Nessuna licenza: premi «Nuova licenza» per avere il codice da dare.</Empty>
        ) : (
          <div className="ag-list">
            {list.map((l) => (
              <div key={l.id} className="ag-row" onClick={() => ctx.setSub({ lic: l.id })} title="Apri la licenza: PC, storico, scadenza">
                <Icon name="key" size={18} />
                <span className="ag-title">
                  <b>{l.holder}</b>
                  <div className="muted small">
                    {l.devicesActive} di {pcs(l.deviceLimit)} · {l.expiresOn ? `scade il ${day(l.expiresOn)}` : 'senza scadenza'}
                    {l.channel === 'prova' ? ' · versioni di prova' : ''}
                    {l.lastSeenAt ? ` · ultimo collegamento ${when(l.lastSeenAt)}` : ''}
                    {l.lastVersion ? ` · versione ${l.lastVersion}` : ''}
                  </div>
                </span>
                <Pill tone={STATUS[l.status].tone} title={STATUS[l.status].tip}>{STATUS[l.status].label}</Pill>
              </div>
            ))}
          </div>
        )}
      </Panel>
      {creating && (
        <LicenseModal
          ctx={ctx}
          onClose={() => setCreating(false)}
          onSaved={(r) => {
            setCreating(false);
            if (r.code) setCode({ holder: r.license.holder, code: r.code });
            void load();
          }}
        />
      )}
      {code && <CodeModal holder={code.holder} code={code.code} onClose={() => setCode(null)} />}
    </div>
  );
}

function LicenseModal({
  ctx,
  license,
  onClose,
  onSaved,
}: {
  ctx: Ctx;
  license?: RacingLicenseDto;
  onClose: () => void;
  onSaved: (r: { license: RacingLicenseDto; code?: string }) => void;
}) {
  const [holder, setHolder] = useState(license?.holder ?? '');
  const [email, setEmail] = useState(license?.email ?? '');
  const [note, setNote] = useState(license?.note ?? '');
  const [deviceLimit, setDeviceLimit] = useState(String(license?.deviceLimit ?? 2));
  const [offline, setOffline] = useState(license ? (license.offlineDays === 0 ? 'sempre' : String(license.offlineDays)) : '14');
  const [expires, setExpires] = useState(license?.expiresOn ?? '');
  const [channel, setChannel] = useState<'prova' | 'amici'>(license?.channel ?? 'amici');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const limit = Number(deviceLimit);
  const valid = holder.trim().length >= 2 && Number.isInteger(limit) && limit >= 1 && limit <= 50;

  const save = async () => {
    setBusy(true);
    setError(null);
    const body = {
      holder: holder.trim(),
      email: email.trim() || null,
      note: note.trim() || null,
      channel,
      deviceLimit: limit,
      offlineDays: offline === 'sempre' ? 0 : Number(offline),
      expiresOn: expires || null,
    };
    try {
      if (license) onSaved({ license: await ctx.api.put<RacingLicenseDto>(`/api/racing/licenses/${license.id}`, body) });
      else onSaved(await ctx.api.post<{ license: RacingLicenseDto; code: string }>('/api/racing/licenses', body));
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={license ? 'Modifica la licenza' : 'Nuova licenza'}
      narrow
      onClose={onClose}
      footer={
        <button type="button" className="btn dark" disabled={busy || !valid} onClick={() => void save()}
          title={license ? 'Salva: i PC ricevono le modifiche al prossimo collegamento' : 'Crea la licenza e mostra il codice da dare'}>
          {license ? 'Salva' : 'Crea e mostra il codice'}
        </button>
      }
    >
      <ErrorBanner message={error} />
      <Field label="Chi la usa" hint="Il nome compare nel programma e nel gioco: «Licenza di …».">
        <input value={holder} maxLength={120} onChange={(e) => setHolder(e.target.value)} placeholder="Nome e cognome" autoFocus />
      </Field>
      <Field label="Su quanti PC" hint="Ogni PC che scrive il codice prende un posto. Pieni i posti, il codice non vale su altri PC.">
        <input type="number" min="1" max="50" value={deviceLimit} onChange={(e) => setDeviceLimit(e.target.value)} />
      </Field>
      <Field label="Scade il" hint="Vuoto = non scade. Dal giorno dopo il gioco non parte più.">
        <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
      </Field>
      <Field label="Senza internet" hint="Per quanti giorni si gioca senza collegarsi. Poi basta aprire il programma con internet.">
        <select value={offline} onChange={(e) => setOffline(e.target.value)}>
          <option value="7">7 giorni</option>
          <option value="14">14 giorni</option>
          <option value="30">30 giorni</option>
          <option value="sempre">Sempre (solo per i PC di Tecnoside)</option>
        </select>
      </Field>
      <Field label="Versioni" hint="«Di prova»: riceve le versioni nuove prima di tutti, per provarle.">
        <select value={channel} onChange={(e) => setChannel(e.target.value as 'prova' | 'amici')}>
          <option value="amici">Quelle per tutti</option>
          <option value="prova">Anche quelle di prova</option>
        </select>
      </Field>
      <Field label="Email (facoltativa)">
        <input type="email" value={email} maxLength={190} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field label="Note (facoltative)">
        <input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </Field>
    </Modal>
  );
}

function CodeModal({ holder, code, onClose }: { holder: string; code: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal title={`Codice per ${holder}`} narrow onClose={onClose}>
      <p className="muted small" style={{ marginTop: 0 }} title="Se lo perdi, dalla licenza crei un codice nuovo: i PC già attivati continuano a giocare.">
        Si vede <b>solo adesso</b>: copialo e mandalo a {holder}.
      </p>
      <div className="guard-code">{code}</div>
      <p className="small">Va scritto una volta sola nel programma «SlipAngle», con internet acceso.</p>
      <button type="button" className="btn dark" title="Copia il codice per incollarlo in un messaggio"
        onClick={() => void navigator.clipboard.writeText(code).then(() => setCopied(true))}>
        <Icon name={copied ? 'check' : 'copy'} size={16} /> {copied ? 'Copiato' : 'Copia il codice'}
      </button>
    </Modal>
  );
}

function LicenseDetail({ ctx, id, onBack, onCode }: { ctx: Ctx; id: number; onBack: () => void; onCode: (c: { holder: string; code: string }) => void }) {
  const [d, setD] = useState<RacingLicenseDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      setD(await ctx.api.get<RacingLicenseDetailDto>(`/api/racing/licenses/${id}`));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [ctx.api, id]);
  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (!d) {
    return (
      <div className="panels single">
        <Panel title="Licenza">
          <ErrorBanner message={error} />
          <div className="muted small">Caricamento…</div>
        </Panel>
      </div>
    );
  }
  const l = d.license;
  const st = STATUS[l.status];
  const suspended = l.status === 'sospesa';
  const activeDevices = d.devices.filter((x) => !x.revokedAt);
  const oldDevices = d.devices.filter((x) => x.revokedAt);

  const suspend = () =>
    void run(async () => {
      if (!suspended && !(await ask(`Sospendere la licenza di ${l.holder}? Il gioco smette di partire sui suoi PC appena si collegano.`))) return;
      await ctx.api.post(`/api/racing/licenses/${id}/${suspended ? 'resume' : 'suspend'}`);
    });
  const renew = () =>
    void run(async () => {
      if (!(await ask('Creare un codice nuovo? Quello vecchio non attiva più PC; i PC già attivati continuano a giocare.'))) return;
      const r = await ctx.api.post<{ code: string }>(`/api/racing/licenses/${id}/code`);
      onCode({ holder: l.holder, code: r.code });
    });
  const release = (deviceId: number, name: string | null) =>
    void run(async () => {
      if (!(await ask(`Liberare il posto di «${name ?? 'questo PC'}»? Quel PC non può più giocare né aggiornare; il posto va a un altro PC.`))) return;
      await ctx.api.del(`/api/racing/licenses/${id}/devices/${deviceId}`);
    });

  return (
    <div className="panels single">
      <Panel
        title={
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="btn icon ghost" title="Torna all’elenco delle licenze" onClick={onBack}>
              <Icon name="chevronLeft" size={18} />
            </button>
            {l.holder}
            <Pill tone={st.tone} title={st.tip}>{st.label}</Pill>
          </span>
        }
        subtitle={`Codice …${l.codeHint} · ${activeDevices.length} di ${pcs(l.deviceLimit)} · ${l.expiresOn ? `scade il ${day(l.expiresOn)}` : 'senza scadenza'} · ${l.offlineDays ? `${l.offlineDays} giorni senza internet` : 'sempre senza internet'}${l.channel === 'prova' ? ' · versioni di prova' : ''}`}
        right={
          <div className="row-actions" style={{ margin: 0 }}>
            <button type="button" className="btn" title="Cambia nome, numero di PC, scadenza, giorni senza internet" onClick={() => setEditing(true)}>
              <Icon name="edit" size={16} /> Modifica
            </button>
            <button type="button" className="btn" title="Serve se il codice è andato perso o l’ha avuto chi non doveva" onClick={renew}>
              <Icon name="refresh" size={16} /> Nuovo codice
            </button>
            <button type="button" className="btn" title={suspended ? 'I PC tornano a giocare al prossimo collegamento' : 'Blocca subito gioco e aggiornamenti su tutti i suoi PC'} onClick={suspend}>
              <Icon name={suspended ? 'play' : 'lock'} size={16} /> {suspended ? 'Riattiva' : 'Sospendi'}
            </button>
          </div>
        }
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {(l.email || l.note) && <p className="small muted" style={{ marginTop: 0 }}>{[l.email, l.note].filter(Boolean).join(' · ')}</p>}
        <h3 className="form-h" title="Ogni PC che ha scritto il codice. Liberando un posto, quel PC smette di giocare.">PC con la licenza</h3>
        {activeDevices.length === 0 ? (
          <Empty icon="server">Nessun PC ancora: il primo che scrive il codice prende il posto.</Empty>
        ) : (
          <div className="ag-list">
            {activeDevices.map((x) => (
              <div key={x.id} className="ag-row" style={{ cursor: 'default' }}>
                <Icon name="server" size={18} />
                <span className="ag-title">
                  <b>{x.pcName || 'PC senza nome'}</b>
                  <div className="muted small">
                    Attivato il {when(x.activatedAt)} · ultimo collegamento {when(x.lastSeenAt)}
                    {x.lastVersion ? ` · versione ${x.lastVersion}` : ''}
                  </div>
                </span>
                <button type="button" className="btn small" title="Questo PC smette di giocare e il posto si libera per un altro" onClick={() => release(x.id, x.pcName)}>
                  Libera il posto
                </button>
              </div>
            ))}
          </div>
        )}
        {oldDevices.length > 0 && (
          <p className="small muted">Posti liberati: {oldDevices.map((x) => `${x.pcName || 'PC'} (${when(x.revokedAt)})`).join(', ')}</p>
        )}
        <h3 className="form-h" title="Le ultime 100 cose successe a questa licenza">Storico</h3>
        <div className="small">
          {d.events.map((e) => (
            <div key={e.id} style={{ display: 'flex', gap: 12, padding: '4px 0', borderBottom: '1px solid var(--line, rgba(127,127,127,.15))' }}>
              <span className="muted" style={{ minWidth: 150 }}>{when(e.at)}</span>
              <span><b>{EVENT[e.kind] ?? e.kind}</b>{e.detail ? ` · ${e.detail}` : e.pcName ? ` · ${e.pcName}` : ''}</span>
            </div>
          ))}
        </div>
      </Panel>
      {editing && (
        <LicenseModal
          ctx={ctx}
          license={l}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            void load();
          }}
        />
      )}
    </div>
  );
}
